import { CheckOutlined, CloseCircleOutlined, DeleteOutlined, LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Form, Input, Table, Tag, Tooltip, Typography } from "antd";
import { ActionStatus } from "common/enums";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import React, { useCallback, useEffect } from "react";

dayjs.extend(utc);
dayjs.extend(timezone);

interface DataSyndicationSourceSubscriptionsProps {
    accounts?: IDataSyndicationSourceSubscription[];
    onSubscriptionSave: (subscription: IDataSyndicationSourceSubscription) => void;
    onDeleteSubscription?: (subscription: IDataSyndicationSourceSubscription) => void;
    readOnly?: boolean;
    subscriptionAction?: Record<string, Record<string, any>>;
}

const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
        case "PENDING":
            return "orange";
        case "CONFIRMED":
            return "success";
        default:
            return "default";
    }
};

const DataSyndicationSourceSubscriptionList: React.FC<DataSyndicationSourceSubscriptionsProps> = ({
    accounts = [],
    onSubscriptionSave,
    onDeleteSubscription,
    readOnly = false,
    subscriptionAction = {},
}) => {
    const [form] = Form.useForm();

    useEffect(() => {
        form.setFieldsValue({
            accounts: accounts.map((account) => ({
                ...account,
                isNew: false,
            })),
        });
    }, [accounts, form]);

    useEffect(() => {
        const currentAccounts = form.getFieldValue("accounts") || [];
        let hasUpdates = false;

        const updatedAccounts = currentAccounts.map((account: IDataSyndicationSourceSubscription) => {
            const action = subscriptionAction[account.subscriptionKey];
            if (action?.status === ActionStatus.SUCCESS && action.subscription) {
                hasUpdates = true;
                return {
                    ...account,
                    ...action.subscription,
                    isNew: false,
                };
            }
            return account;
        });

        if (hasUpdates) {
            form.setFieldsValue({
                accounts: updatedAccounts,
            });
        }
    }, [subscriptionAction, form]);

    const handleSaveSubscription = useCallback(
        async (record: IDataSyndicationSourceSubscription, index: number) => {
            try {
                const rowValues = await form.validateFields([["accounts", index, "subscriptionTenantName"]]);

                const currentAccounts = form.getFieldValue("accounts") || [];
                const updatedAccounts = currentAccounts.map((account: IDataSyndicationSourceSubscription, i: number) => {
                    if (i === index) {
                        return {
                            ...account,
                            ...rowValues[index],
                            isNew: false,
                        };
                    }
                    return account;
                });

                onSubscriptionSave(updatedAccounts[index]);
            } catch (error: any) {
                console.error("Validation failed:", error);
                if (error?.fields) {
                    // Handle field-specific errors
                    Object.entries(error.fields).forEach(([fieldName, message]) => {
                        form.setFields([
                            {
                                name: ["accounts", index, fieldName],
                                errors: [message as string],
                                touched: true,
                            },
                        ]);
                    });
                }
            }
        },
        [form, onSubscriptionSave],
    );

    const getActionButton = useCallback(
        (record: IDataSyndicationSourceSubscription, index: number) => {
            const subscriptionState = subscriptionAction[record.subscriptionKey]?.status;
            let error = subscriptionAction[record.subscriptionKey]?.error;
            let errorMessage = error?.message || "Save failed. Click to retry.";

            if (subscriptionState === ActionStatus.PENDING) {
                return (
                    <Tooltip title="Saving...">
                        <Tag icon={<LoadingOutlined />} color="processing">
                            Saving
                        </Tag>
                    </Tooltip>
                );
            }

            if (subscriptionState === ActionStatus.FAILED) {
                // Handle field-specific errors when showing failed state
                if (error?.fields) {
                    Object.entries(error.fields).forEach(([fieldName, message]) => {
                        form.setFields([
                            {
                                name: ["accounts", index, fieldName],
                                errors: [message as string],
                                touched: true,
                            },
                        ]);
                    });
                }
                return (
                    <Tooltip title={errorMessage}>
                        <Tag icon={<CloseCircleOutlined />} color="error" style={{ cursor: "pointer" }} onClick={() => handleSaveSubscription(record, index)}>
                            Failed
                        </Tag>
                    </Tooltip>
                );
            }

            if (subscriptionState === ActionStatus.SUCCESS) {
                return (
                    <Tooltip title="Successfully saved">
                        <Tag color="success">Saved</Tag>
                    </Tooltip>
                );
            }

            if (record.isNew) {
                return (
                    <Tooltip title="Click to save">
                        <Tag icon={<CheckOutlined />} color="blue" style={{ cursor: "pointer" }} onClick={() => handleSaveSubscription(record, index)}>
                            Save
                        </Tag>
                    </Tooltip>
                );
            }
        },
        [handleSaveSubscription, subscriptionAction, form],
    );

    const handleDelete = useCallback(
        (record: IDataSyndicationSourceSubscription, index: number) => {
            if (record.subscriptionId) {
                onDeleteSubscription?.(record);
            } else {
                const currentAccounts = form.getFieldValue("accounts") || [];
                form.setFieldsValue({
                    accounts: currentAccounts.filter((_: any, i: number) => i !== index),
                });
            }
        },
        [form, onDeleteSubscription],
    );

    const columns = [
        {
            title: "Account Name",
            dataIndex: "subscriptionTenantName",
            key: "subscriptionTenantName",
            render: (_: any, record: IDataSyndicationSourceSubscription, index: number) => (
                <Form.Item
                    name={[index, "subscriptionTenantName"]}
                    rules={[
                        {
                            required: true,
                            message: "Please enter account name",
                        },
                    ]}
                    style={{ margin: 0 }}
                >
                    <Input readOnly={readOnly || !!record.status} style={{ width: "100%" }} />
                </Form.Item>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (_: any, record: IDataSyndicationSourceSubscription) => <Tag color={getStatusColor(record.status)}>{record.status}</Tag>,
        },
        {
            title: "Created Date",
            dataIndex: "createdDate",
            key: "createdDate",
            render: (text: string, record: IDataSyndicationSourceSubscription) => {
                const formattedDate = text ? dayjs(text).format("YYYY-MM-DD HH:mm:ss") : "-";
                return <span>{formattedDate}</span>;
            },
        },
        {
            key: "action",
            width: 80,
            render: (_: any, record: IDataSyndicationSourceSubscription, index: number) => getActionButton(record, index),
        },
        {
            key: "delete",
            width: 50,
            render: (_: any, record: IDataSyndicationSourceSubscription, index: number) => (
                <Tooltip title="Delete">
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record, index)} disabled={readOnly} />
                </Tooltip>
            ),
        },
    ];

    const handleAddAccount = useCallback(() => {
        const currentAccounts = form.getFieldValue("accounts") || [];
        const newAccount = {
            subscriptionKey: Date.now(),
            subscriptionTenantName: "",
            status: "",
            createdDate: "",
            isNew: true,
        };
        form.setFieldsValue({
            accounts: [...currentAccounts, newAccount],
        });
    }, [form]);

    return (
        <Form
            form={form}
            initialValues={{
                accounts: accounts.map((account) => ({
                    ...account,
                    isNew: false,
                })),
            }}
            className="flex size-full flex-1 flex-col overflow-auto"
        >
            <Card
                variant="outlined"
                className="flex size-full flex-1 flex-col overflow-hidden"
                title={
                    <div className="flex justify-between">
                        <Typography.Title level={5} style={{ marginBottom: 0 }}>
                            Accounts
                        </Typography.Title>
                        <Button type="default" onClick={handleAddAccount} disabled={readOnly} icon={<PlusOutlined />}>
                            Add another account
                        </Button>
                    </div>
                }
                classNames={{
                    body: "!p-0 overflow-auto flex flex-1 flex-col size-full scroll-webkit",
                }}
            >
                <Form.List name="accounts">
                    {(fields) => (
                        <>
                            <Table
                                dataSource={form.getFieldValue("accounts") || []}
                                columns={columns}
                                pagination={false}
                                rowKey={(record: IDataSyndicationSourceSubscription) => record.subscriptionKey}
                                tableLayout="auto"
                            />
                        </>
                    )}
                </Form.List>
            </Card>
        </Form>
    );
};

export default DataSyndicationSourceSubscriptionList;
